import React, { useEffect, useState, useCallback } from 'react';
import {
  Checkbox,
  Form,
  message,
  Table,
  Tooltip,
  Typography,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Switch,
  Button,
  Alert,
} from 'antd';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { apiUrl } from '../../../common/url';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { SaveOutlined } from '@ant-design/icons';

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);

type FormItemType =
  | 'text'
  | 'number'
  | 'decimal'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'selection'
  | 'multi-selection'
  | 'textarea';

interface Validation {
  min?: number;
  max?: number;
  maxLength?: number;
}

interface CategoryColumn {
  name: string;
  description?: string;
  type: FormItemType;
  options?: Array<string | number>;
  isRequired?: boolean;
  validation?: Validation;
}

interface CategoryData {
  title: string;
  description: string;
  columns: CategoryColumn[];
  data: Record<string, any>[];
}

interface BackendResponse {
  fixedColumns: {
    Key: number;
    [key: string]: any;
  }[];
  categories: CategoryData[];
}

interface DataCollBasisEditProps {
  client: { id: string; name?: string; token: string; permissions: string[] };
  dataCollBasisName: string;
  setLoading?: (loading: boolean) => void;
  isEditable: boolean;
}

const DataCollBasisEdit: React.FC<DataCollBasisEditProps> = ({
  client,
  dataCollBasisName,
  setLoading,
  isEditable,
}) => {
  const [visibleCategories, setVisibleCategories] = useState<string[]>([]);
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [columns, setColumns] = useState<ColumnsType<Record<string, any>>>([]);
  const [totalWidth, setTotalWidth] = useState(0);
  const [savingRows, setSavingRows] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const handleCellChange = (key: number, categoryTitle: string, fieldName: string, value: any) => {
    const actualValue = value?.target?.value !== undefined ? value.target.value : value;
    setData((prev) =>
      prev.map((row) =>
        row.Key === key
          ? {
              ...row,
              [fieldName]: actualValue,
            }
          : row
      )
    );
  };

  const handleSave = (record: Record<string, any>) => {
    if (!record) {
      message.error('Keine Zeilendaten gefunden', 6);
      return;
    }

    // Clean the record data
    const cleanRecord = { ...record };

    // Remove the lowercase 'key' if it exists to avoid confusion
    if (cleanRecord.key !== undefined) {
      delete cleanRecord.key;
    }

    // Remove any undefined values
    Object.keys(cleanRecord).forEach((key) => {
      if (cleanRecord[key] === undefined) {
        delete cleanRecord[key];
      }
    });

    const requestData = {
      data: cleanRecord,
      table_name: dataCollBasisName,
    };

    setSavingRows((prev) => ({ ...prev, [record.Key]: true }));

    axios
      .post(`${apiUrl}/b2zero/${client.id}/update_data_coll_basis_row`, requestData, {
        headers: { Authorization: `Bearer ${client.token}` },
      })
      .then(() => {
        message.success('Änderungen erfolgreich gespeichert', 6);
      })
      .catch((error: any) => {
        message.error(
          'Fehler beim Speichern der Änderungen: ' + (error.response?.data?.error || error.message),
          6
        );
        Sentry.captureException(error);
      })
      .finally(() => {
        setSavingRows((prev) => ({ ...prev, [record.Key]: false }));
      });
  };

  const actionColumn: ColumnsType<Record<string, any>> = [
    {
      title: 'Aktionen',
      key: 'actions',
      fixed: 'right' as 'right',
      width: 100,
      render: (_, record) => (
        <Tooltip title="Speichern">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => handleSave(record)}
            loading={savingRows[record.Key]}
          />
        </Tooltip>
      ),
    },
  ];

  const renderFormItem = (
    column: CategoryColumn,
    record: Record<string, any>,
    categoryTitle: string
  ) => {
    if (!isEditable) return <span>{record[column.name]}</span>;

    const commonProps = {
      value:
        column.type === 'date' || column.type === 'datetime'
          ? record[column.name]
            ? dayjs(record[column.name])
            : null
          : record[column.name],
      onChange: (value: any) => handleCellChange(record.Key, categoryTitle, column.name, value),
      style: { width: '100%' },
    };

    switch (column.type) {
      case 'text':
        return (
          <Input
            {...commonProps}
            maxLength={column.validation?.maxLength}
            required={column.isRequired}
          />
        );
      case 'number':
        return (
          <InputNumber {...commonProps} min={column.validation?.min} max={column.validation?.max} />
        );
      case 'decimal':
        return (
          <InputNumber
            {...commonProps}
            step={0.01}
            min={column.validation?.min}
            max={column.validation?.max}
          />
        );
      case 'date':
        return <DatePicker {...commonProps} />;
      case 'datetime':
        return <DatePicker showTime {...commonProps} />;
      case 'boolean':
        return <Switch checked={!!commonProps.value} onChange={commonProps.onChange} />;
      case 'selection':
        return (
          <Select
            {...commonProps}
            options={column.options?.map((option) => ({ value: option, label: option }))}
            showSearch
          />
        );
      case 'multi-selection':
        return (
          <Select
            {...commonProps}
            mode="multiple"
            options={column.options?.map((option) => ({ value: option, label: option }))}
            showSearch
          />
        );
      case 'textarea':
        return (
          <Input.TextArea
            {...commonProps}
            maxLength={column.validation?.maxLength}
            required={column.isRequired}
          />
        );
      default:
        return <span>{record[column.name]}</span>;
    }
  };

  const fetchDataCollBasis = useCallback(() => {
    if (!dataCollBasisName) return;
    setLoading?.(true);
    axios
      .get(`${apiUrl}/b2zero/${client.id}/get_data_coll_basis`, {
        params: {
          dataCollBasisName: dataCollBasisName,
        },
        headers: { Authorization: `Bearer ${client.token}` },
      })
      .then((response) => {
        const { fixedColumns, categories } = response.data as BackendResponse;

        const fixedColumnsDef: ColumnsType<Record<string, any>> = Object.keys(fixedColumns[0]).map(
          (key, index) => ({
            title: key,
            dataIndex: key,
            key: `fixed-${key}-${index}`,
            fixed: 'left' as 'left',
            width: key === 'Key' ? 75 : 200,
          })
        );

        const categoryColumns: ColumnsType<Record<string, any>> = categories.map((category) => ({
          title: <Tooltip title={category.description}>{category.title}</Tooltip>,
          key: category.title,
          children: category.columns.map((col) => ({
            title: <Tooltip title={col.description}>{col.name}</Tooltip>,
            dataIndex: col.name,
            key: `${category.title}-${col.name}`,
            render: (_, record) => renderFormItem(col, record, category.title),
          })),
        }));

        const hasEditableColumns = categories.some((category) =>
          category.columns.some((col) => col.type)
        );

        setColumns([
          ...fixedColumnsDef,
          ...categoryColumns,
          ...(hasEditableColumns ? actionColumn : []),
        ]);
        const processedData = fixedColumns.map((fixedCol, fixedColIndex) => ({
          ...fixedCol,
          key: `fixedCol-${fixedCol.Key}-${fixedColIndex}`,
          ...categories.reduce((acc, category) => {
            const categoryData = category.data.find((data) => data.Key === fixedCol.Key);
            return { ...acc, ...categoryData };
          }, {}),
        }));

        setData(processedData);
        const visibleCategoriesNew = categories.length > 0 ? [categories[0].title] : [];
        setVisibleCategories(visibleCategoriesNew);
        setTotalWidth(calculateTotalWidth(visibleCategoriesNew));
      })
      .catch((error) => {
        message.error('Fehler beim Abruf der Stammdatentabelle: ' + error.message, 6);
        setError(error.message);
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setExtra('hint', 'Fehler beim Abrufen der Stammdatentabelle: ' + error.message);
          Sentry.captureException(error);
        });
      })
      .finally(() => {
        setLoading?.(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, client.token, dataCollBasisName, setLoading]);

  useEffect(() => {
    if (dataCollBasisName) {
      fetchDataCollBasis();
    }
  }, [fetchDataCollBasis, dataCollBasisName]);

  useEffect(() => {
    setTotalWidth(calculateTotalWidth(visibleCategories));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCategories, columns]);

  const filteredColumns = columns.filter((col) =>
    'children' in col ? visibleCategories.includes(col.key as string) : true
  );

  const calculateTotalWidth = (visibleCategories: string[]) => {
    const fixedColumnsWidth = columns
      .filter((col) => !('children' in col))
      .reduce((acc, col) => {
        if (col.title === 'key') {
          return acc + 75;
        }
        return acc + 200;
      }, 0);

    const visibleCategoryColumnsWidth = columns
      .filter((col) => 'children' in col && visibleCategories.includes(col.key as string))
      .reduce((acc, col) => {
        if ('children' in col) {
          return acc + (col.children?.length || 0) * 200;
        }
        return acc;
      }, 0);

    return fixedColumnsWidth + visibleCategoryColumnsWidth;
  };

  if (error) {
    return (
      <Alert
        message="Fehler beim Abrufen der Stammdatentabelle"
        description={error}
        type="error"
        showIcon
        action={
          <Button
            type="primary"
            onClick={() => {
              setError(null);
              fetchDataCollBasis();
            }}
          >
            Erneut versuchen
          </Button>
        }
      />
    );
  }

  return (
    <Table
      columns={filteredColumns}
      dataSource={data}
      rowKey="Key"
      bordered
      scroll={{ x: totalWidth }}
      title={() => (
        <>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Stammdatentabelle {dataCollBasisName ? `(${dataCollBasisName})` : ''}
          </Typography.Title>
          <Form.Item label="Kategorien" style={{ marginBottom: 0 }}>
            <Checkbox.Group
              options={columns
                .filter((col) => 'children' in col)
                .map((col, index) => ({
                  label: col.title as React.ReactNode,
                  value: col.key as string,
                  key: `checkbox-${index}`,
                }))}
              value={visibleCategories}
              onChange={(checkedValues) => {
                setVisibleCategories(checkedValues as string[]);
                setTotalWidth(calculateTotalWidth(checkedValues));
              }}
            />
          </Form.Item>
        </>
      )}
    />
  );
};

export default DataCollBasisEdit;
